import CommunityAccess from 'enums/community/access';
import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Observable, ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import CommunityAccessManager from 'managers/community/access';
import { CurrentUserManager } from 'managers/currentUser';
import device from 'managers/device';
import SimpleTopicsManager from 'managers/discussion/simpleTopics';
import { IWCCStorageManager, StorageArg } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import { SimpleTopic } from 'models/simpleTopic';
import { SimpleTopicsGroup } from 'models/simpleTopicsGroup';
import CommunityTopicsManager from '../../../../managers/community/communityTopicsManager';
import CommunityWidgetVM, { CommunityWidgetModel, CommunityWidgetVMConfig } from '../base/base';
import CommunityNavigationWidgetGroupsBuilder from './builders/groups';
import { default as CommunityNavigationWidgetMode, default as modes } from './enums/modes';
import CommunityNavigationWidgetLinkType from './enums/types';
import CommunityNavigationWidgetItemModel from './item';
import { CommunityNavigationWidgetLinkConfig, CommunityNavigationWidgetLinkModel } from './link';
import './navigation.html';
import './navigation.scss';

components
    .registerLocal('community-navigation-settings', () => require('./settings'))
    .registerLocal('community-navigation-desktop', () => require('./desktop/navigation'))
    .registerLocal('community-navigation-mobile', () => require('./mobile/navigation'))
    .registerLocal('community-navigation-link', () => require('./link'));

const { discussionId, discussionId: communityId } = settings;

const allowedAccessTypes = [
    CommunityAccess.public,
    CommunityAccess.membersonly
]

const groupsBuilder = CommunityNavigationWidgetGroupsBuilder.instance;

export interface CommunityNavigationWidgetVMConfig extends CommunityWidgetVMConfig {
    IsUserLoggedIn?: boolean
    LogOut?: boolean
    Mode?: CommunityNavigationWidgetMode
    Links?: Array<any>
    MobileLinks?: Array<any>

    isUserLoggedIn?: boolean
    logOut?: boolean
    mode?: CommunityNavigationWidgetMode
    links?: Array<CommunityNavigationWidgetLinkConfig>
    mobileLinks?: Array<CommunityNavigationWidgetLinkConfig>
}

export class CommunityNavigationWidgetModel extends CommunityWidgetModel<CommunityNavigationWidgetVMConfig> {
    isUserLoggedIn: Observable<boolean>
    mode: Observable<CommunityNavigationWidgetMode>
    showLogOutLink: Observable<boolean>

    links: ObservableArray<CommunityNavigationWidgetLinkModel>
    mobileLinks: ObservableArray<CommunityNavigationWidgetLinkModel>

    constructor(data?: CommunityNavigationWidgetVMConfig) {
        super(data);

        this.isUserLoggedIn = ko.observable(data?.isUserLoggedIn ?? settings.loggedInNavigation);
        this.mode = this.createField(data, 'mode', CommunityNavigationWidgetMode.auto);
        this.showLogOutLink = this.createField(data, 'logOut', true);

        this.links = this.createCollection(data, 'links', jsonLink => new CommunityNavigationWidgetLinkModel(jsonLink));
        this.mobileLinks = this.createCollection(data, 'mobileLinks', jsonLink => new CommunityNavigationWidgetLinkModel(jsonLink));
    }

    protected override normalizeData(data: CommunityNavigationWidgetVMConfig) {
        super.normalizeData(data);

        data.isUserLoggedIn ??= data.IsUserLoggedIn;
        data.logOut ??= data.LogOut;
        data.mode ??= data.Mode;
        data.links ??= data.Links;
        data.mobileLinks ??= data.MobileLinks;
    }
}

injectable()
export default class CommunityNavigationWidgetVM extends CommunityWidgetVM<CommunityNavigationWidgetModel> {
    private logOutLink = new CommunityNavigationWidgetLinkModel({ title: labels.LogOut, type: CommunityNavigationWidgetLinkType.logout, isProtected: true });

    private currentUserStatus: Subscribable<number | undefined>
    private groups: Subscribable<Array<SimpleTopicsGroup>>
    private topics: Subscribable<Array<SimpleTopic>>

    private hasAccess: Subscribable<boolean>
    private isLoggedIn: Subscribable<boolean>

    static Model = CommunityNavigationWidgetModel

    mode: Subscribable<CommunityNavigationWidgetMode>

    links: Subscribable<Array<CommunityNavigationWidgetItemModel>>
    mobileLinks: Subscribable<Array<CommunityNavigationWidgetItemModel>>

    isDemo: Subscribable<boolean>
    isMobile: Subscribable<boolean>
    isDesktop: Subscribable<boolean>
    showLogOutLink: Subscribable<boolean>

    beforeInit: Action

    constructor(
        @inject(wccModules.componentConfig) model: CommunityNavigationWidgetModel,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(model, info);

        this.isDemo = model.demo;

        this.mode = model.mode;
        this.showLogOutLink = model.showLogOutLink;

        this.isMobile = ko.pureComputed(() => this.checkIfMobile());
        this.isDesktop = this.isMobile.not();

        const accessManager = storage.get(CommunityAccessManager, { communityId });
        const access = accessManager.pluck(m => m.access, CommunityAccess.none);
        this.hasAccess = ko.pureComputed(() => this.isDemo() || allowedAccessTypes.includes(access()));

        const userManager = storage.get(CurrentUserManager, { discussionId });
        const user = userManager.pluck(m => m.person);
        this.currentUserStatus = user.pluck(u => u.status);
               
        
        const topicsManager = storage.get(CommunityTopicsManager, { discussionId });
        this.groups = topicsManager.pluck(m => m.groups, []);
        this.topics = topicsManager.pluck(m => m.topics, []);


        this.isLoggedIn = ko.pureComputed(() => this.isDemo() ? model.isUserLoggedIn() : user() != undefined);

        const links = ko.pureComputed(() => this.getLinks());

        this.links = links
            .filter(link => this.isLinkAllowed(link))
            .pluck(link => this.unwrapLink(link))
            .flatten()
            .mapSingle(links => groupsBuilder.build(links), null, true)
            .cache('title', (oldItem, newItem) => oldItem.update(newItem.toJson(false)))
            .extend({ deferred: true });
                
        const mobileLinks = ko.pureComputed(() => this.getMobileLinks());

        this.mobileLinks = mobileLinks
            .filter(link => this.isLinkAllowed(link))
            .pluck(link => this.unwrapLink(link))
            .flatten()
            .mapSingle(links => groupsBuilder.build(links), null, true)
            .cache('title', (oldItem, newItem) => oldItem.update(newItem.toJson(false)))
            .extend({ deferred: true });

        this.beforeInit = () => {
            effects.register((links, mobileLinks, isDemo) => {
                links.forEach(link => link.demo(isDemo));
                mobileLinks.forEach(link => link.demo(isDemo));
            }, [this.links, this.mobileLinks, this.isDemo]);
        }

        ko.applyBindingsToNode(info.element, {
            'css': {
                'community__navigation': true
            }
        });
    }

    injectSettingsNodeInto(container: Node) {
        container.appendChild(DOMHelpers.createComponent('community-navigation-settings', { model: this }));
    }

    private checkIfMobile() {
        switch (this.mode()) {
            case modes.desktop: return false;
            case modes.mobile: return true;
            default: return device.isMobile();
        }
    }

    private getLinks() {
        let links = this.model.links();

        if (this.showLogOutLink())
            links = [...links, this.logOutLink];

        return links;
    }

    private getMobileLinks() {
        let links = this.model.mobileLinks();

        if (links.length == 0)
            links = this.model.links();

        if (this.showLogOutLink())
            links = [...links, this.logOutLink];

        return links;
    }

    private unwrapLink(link: CommunityNavigationWidgetLinkModel) {
        switch (link.type()) {
            case CommunityNavigationWidgetLinkType.activities: return this.unwrapActivitiesLink(link);
            case CommunityNavigationWidgetLinkType.activitiesGroup: return this.unwrapActivitiesGroupLink(link);
            case CommunityNavigationWidgetLinkType.activity: return this.unwrapActivityLink(link);
            case CommunityNavigationWidgetLinkType.todo: return this.unwrapToDoLink(link);
            default: return link;
        }
    }

    private unwrapActivitiesLink(link: CommunityNavigationWidgetLinkModel) {
        return this.groups().flatMap(group => {
            return group.topics().map(topic => {
                var adhocDiscussion = discussionId != topic.discussionId();
                                                
                return new CommunityNavigationWidgetLinkModel({
                    title: ko.pureComputed(() => `${link.title()}/{group:${group.id()}}/{topic:${topic.id()}}`),
                    route: ko.pureComputed(() => adhocDiscussion ? `gototaskfromcommunity/${topic.taskMemberCode()}#topic/${topic.discussionId()}/${topic.id()}` : `topic/${discussionId}/${topic.id()}`),
                    topicId: topic.id,
                    toDoCount: topic.todoCountForUI,
                    css: link.css,
                    type: adhocDiscussion ? CommunityNavigationWidgetLinkType.communityProject : CommunityNavigationWidgetLinkType.url
                });
            });
        });
    }

    private unwrapActivitiesGroupLink(link: CommunityNavigationWidgetLinkModel) {
        const group = this.groups().find(group => group.id() == link.topicId());

        if (group != undefined) {
            return group.topics().map(topic => {
                return new CommunityNavigationWidgetLinkModel({
                    title: ko.pureComputed(() => `${link.title()}/{topic:${topic.id()}}`),
                    route: ko.pureComputed(() =>`topic/${discussionId}/${topic.id()}`),
                    topicId: topic.id,
                    toDoCount: topic.todoCountForUI,
                    css: link.css
                });
            });
        }

        return [];
    }

    private unwrapActivityLink(link: CommunityNavigationWidgetLinkModel) {
        var topic = this.topics().find(topic => topic.id() === link.topicId());

        if (topic != undefined) {
            return new CommunityNavigationWidgetLinkModel({
                title: link.title,
                route: ko.pureComputed(() =>`topic/${discussionId}/${topic?.id()}`),
                topicId: topic.id,
                toDoCount: topic.todoCountForUI,
                css: link.css
            });
        }

        return new Array<CommunityNavigationWidgetLinkModel>();
    }

    private unwrapToDoLink(link: CommunityNavigationWidgetLinkModel) {
        return new CommunityNavigationWidgetLinkModel({
            title: link.title,
            type: link.type,
            toDoCount: this.topics.count(topic => topic.shouldBeAnswered() ? 1 : 0),
            css: link.css
        });
    }

    private isLinkAllowed(link: CommunityNavigationWidgetLinkModel) {
        return (this.hasAccess() || link.type() === CommunityNavigationWidgetLinkType.logout) &&
            link.isProtected() == this.isLoggedIn() &&
            (this.isDemo() || link.accessRestrictions().length == 0 || link.accessRestrictions().includes(<number>this.currentUserStatus()));
    }
}